import React, { useState } from 'react';
import { 
  Box, 
  Grid, 
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Resource, ResourceCategory } from '../../types';
import { ResourceCard } from './components/ResourceCard';
import { ReferralForm } from './components/ReferralForm';

const mockResources: Resource[] = [
  {
    id: '1',
    name: 'Community Health Center',
    category: ResourceCategory.HEALTHCARE,
    description: 'Provides free or low-cost healthcare services to students and families',
    contact: '(555) 123-4567',
    address: '123 Main St, Anytown, USA',
    website: 'https://example.com',
    services: ['Primary Care', 'Dental', 'Vision', 'Mental Health']
  },
  {
    id: '2',
    name: 'Academic Success Center',
    category: ResourceCategory.ACADEMIC_SUPPORT,
    description: 'Tutoring and academic support services for students',
    contact: '(555) 987-6543',
    services: ['Tutoring', 'Study Skills', 'Test Prep', 'College Counseling']
  }
];

export const ResourceDirectory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory | 'all'>('all');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isReferralFormOpen, setIsReferralFormOpen] = useState(false);

  const handleMakeReferral = (resourceId: string) => {
    const resource = mockResources.find(r => r.id === resourceId);
    if (resource) {
      setSelectedResource(resource);
      setIsReferralFormOpen(true);
    }
  };

  const handleReferralSubmit = (referral: any) => {
    console.log('New referral:', referral);
    setIsReferralFormOpen(false);
  };

  const filteredResources = mockResources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 2,
        height: '100%'
      }}
    >
      <Box 
        component="div" 
        sx={{ 
          typography: 'h4',
          fontWeight: 500
        }}
      >
        Resource Directory
      </Box>
      
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value as ResourceCategory | 'all')}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Mental Health">Mental Health</MenuItem>
                <MenuItem value="Academic Support">Academic Support</MenuItem>
                <MenuItem value="Social Services">Social Services</MenuItem>
                <MenuItem value="Housing">Housing</MenuItem>
                <MenuItem value="Transportation">Transportation</MenuItem>
                <MenuItem value="Legal Services">Legal Services</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        <Grid container spacing={3}>
          {filteredResources.map((resource) => (
            <Grid item xs={12} md={6} key={resource.id}>
              <ResourceCard
                resource={resource}
                onMakeReferral={handleMakeReferral}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <ReferralForm
        open={isReferralFormOpen}
        resource={selectedResource}
        onClose={() => {
          setIsReferralFormOpen(false);
          setSelectedResource(null);
        }}
        onSubmit={handleReferralSubmit}
      />
    </Box>
  );
};